/*
 * How a job repeats, like Jobber (owner shots, 2026-10-31): a Repeats list made from the visit's date (Daily, Weekly on
 * Mondays, Every 2 weeks on Mondays, Monthly on the 21st, As needed, Custom), and the Set up recurring schedule box for
 * Custom (every N days, weeks, months or years; months by date or by weekday). Plus Apply visit settings to future visits.
 */
import { useState } from "react";
import {
  choiceOf,
  isoDay,
  LAST_DAY,
  LAST_WEEK,
  ordinal,
  repeatChoices,
  repeatWords,
  REPEAT_UNITS,
  type RepeatRule,
  type RepeatUnit,
  type RepeatValue,
  type VisitFutureBody,
} from "@steward/shared";
import { Combobox } from "./fields";
import { Modal } from "./overlays";
import { Button } from "./parts";
import { shortDate } from "./format";

const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const UNIT_WORDS: Record<RepeatUnit, string> = { day: "Day", week: "Week", month: "Month", year: "Year" };
export const ONE_TIME: RepeatValue = { frequency: "one-time", repeatDay: null, repeatRule: null };

/** The Repeats list. date: the first visit's day, which names the choices (Weekly on Mondays). */
export function RepeatPicker({
  value,
  date,
  onChange,
  oneTime = true,
}: {
  value: RepeatValue;
  date: string;
  onChange: (v: RepeatValue) => void;
  /** Offer One time (the job form does; a visit of a repeating job does not). */
  oneTime?: boolean;
}) {
  const [custom, setCustom] = useState(false);
  const choices = repeatChoices(date);
  const key = value.frequency === "one-time" ? "one-time" : choiceOf(value, date);
  const customWords = key === "custom" ? repeatWords({ ...value, startDate: date }) : null;
  return (
    <>
      <Combobox
        label="Repeats"
        value={key}
        onChange={(k) => {
          if (k === "custom") setCustom(true);
          else if (k === "one-time") onChange(ONE_TIME);
          else {
            const c = choices.find((x) => x.key === k);
            if (c) onChange(c.value);
          }
        }}
        options={[
          ...(oneTime ? [{ value: "one-time", label: "One time" }] : []),
          ...choices.map((c) => ({ value: c.key, label: c.label })),
          { value: "custom", label: customWords ? `Custom (${customWords})` : "Custom..." },
        ]}
      />
      {custom ? (
        <RepeatSheet
          date={date}
          rule={value.repeatRule ?? ruleOf(value, date)}
          onClose={() => setCustom(false)}
          onSave={(r) => {
            setCustom(false);
            onChange({ frequency: "custom", repeatDay: null, repeatRule: r });
          }}
        />
      ) : null}
    </>
  );
}

/** A repeat that is not custom, as a custom rule, so the box opens on what the job does now. */
function ruleOf(v: RepeatValue, date: string): RepeatRule {
  const day = v.repeatDay ?? isoDay(date);
  switch (v.frequency) {
    case "daily":
      return { every: 1, unit: "day" };
    case "biweekly":
      return { every: 2, unit: "week", weekdays: [day] };
    case "monthly":
    case "bimonthly":
      return {
        every: v.frequency === "monthly" ? 1 : 2,
        unit: "month",
        monthWeekdays: [{ nth: Math.min(LAST_WEEK, Math.ceil(Number(date.slice(8, 10)) / 7)), day: isoDay(date) }],
      };
    default:
      return { every: 1, unit: "week", weekdays: [day] };
  }
}

/** Set up recurring schedule: every N days, weeks, months or years. */
export function RepeatSheet({
  date,
  rule,
  onClose,
  onSave,
}: {
  date: string;
  rule: RepeatRule;
  onClose: () => void;
  onSave: (r: RepeatRule) => void;
}) {
  const [every, setEvery] = useState(String(rule.every));
  const [unit, setUnit] = useState<RepeatUnit>(rule.unit);
  const [weekdays, setWeekdays] = useState<number[]>(rule.weekdays?.length ? rule.weekdays : [isoDay(date)]);
  const [byWeekday, setByWeekday] = useState(Boolean(rule.monthWeekdays?.length));
  const [monthDays, setMonthDays] = useState<number[]>(rule.monthDays?.length ? rule.monthDays : [Number(date.slice(8, 10))]);
  const [monthWeekdays, setMonthWeekdays] = useState(
    rule.monthWeekdays?.length
      ? rule.monthWeekdays
      : [{ nth: Math.min(LAST_WEEK, Math.ceil(Number(date.slice(8, 10)) / 7)), day: isoDay(date) }],
  );
  const n = Math.floor(Number(every));
  const bad = !Number.isFinite(n) || n < 1 || n > 99 ? "Use a number from 1 to 99." : null;
  const flip = <T,>(list: T[], x: T, same: (a: T, b: T) => boolean) =>
    list.some((y) => same(x, y)) ? list.filter((y) => !same(x, y)) : [...list, x];
  const out: RepeatRule =
    unit === "week"
      ? { every: n, unit, weekdays: [...weekdays].sort() }
      : unit === "month"
        ? byWeekday
          ? { every: n, unit, monthWeekdays }
          : { every: n, unit, monthDays: [...monthDays].sort((a, b) => a - b) }
        : { every: n, unit };
  const empty =
    (unit === "week" && !weekdays.length) || (unit === "month" && (byWeekday ? !monthWeekdays.length : !monthDays.length));
  return (
    <Modal
      title="Set up recurring schedule"
      open
      onClose={onClose}
      footer={
        <>
          <Button
            type="tertiary"
            onClick={() => {
              setWeekdays([]);
              setMonthDays([]);
              setMonthWeekdays([]);
            }}
          >
            Clear
          </Button>
          <span className="repeat-push" />
          <Button type="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={Boolean(bad) || empty} onClick={() => onSave(out)}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="repeat-every">
          <span>Every</span>
          <input
            className="repeat-n"
            aria-label="How many"
            aria-invalid={bad ? true : undefined}
            inputMode="numeric"
            value={every}
            onChange={(e) => setEvery(e.target.value)}
          />
          <select aria-label="Days, weeks, months or years" value={unit} onChange={(e) => setUnit(e.target.value as RepeatUnit)}>
            {REPEAT_UNITS.map((u) => (
              <option key={u} value={u}>
                {UNIT_WORDS[u]}
              </option>
            ))}
          </select>
          {unit === "week" || unit === "month" ? <span>on</span> : null}
        </div>
        {bad ? <span className="description error">{bad}</span> : null}
        {unit === "week" ? (
          <div className="repeat-grid week" role="group" aria-label="Days of the week">
            {DAY_LETTERS.map((l, i) => (
              <button
                key={i}
                type="button"
                aria-label={DAY_NAMES[i]}
                aria-pressed={weekdays.includes(i + 1)}
                onClick={() => setWeekdays(flip(weekdays, i + 1, (a, b) => a === b))}
              >
                {l}
              </button>
            ))}
          </div>
        ) : null}
        {unit === "month" ? (
          <>
            <div className="choice" role="radiogroup" aria-label="Day of month or day of week">
              <button type="button" role="radio" aria-checked={!byWeekday} onClick={() => setByWeekday(false)}>
                <b>Day of month</b>
              </button>
              <button type="button" role="radio" aria-checked={byWeekday} onClick={() => setByWeekday(true)}>
                <b>Day of week</b>
              </button>
            </div>
            {byWeekday ? (
              <div className="repeat-grid nth" role="group" aria-label="Weekdays of the month">
                {[1, 2, 3, 4, LAST_WEEK].map((nth) => (
                  <div key={nth} className="repeat-nth">
                    <span>{nth === LAST_WEEK ? "Last" : ordinal(nth)}</span>
                    {DAY_LETTERS.map((l, i) => {
                      const on = monthWeekdays.some((m) => m.nth === nth && m.day === i + 1);
                      return (
                        <button
                          key={i}
                          type="button"
                          aria-label={`${nth === LAST_WEEK ? "Last" : ordinal(nth)} ${DAY_NAMES[i]!}`}
                          aria-pressed={on}
                          onClick={() =>
                            setMonthWeekdays(
                              flip(monthWeekdays, { nth, day: i + 1 }, (a, b) => a.nth === b.nth && a.day === b.day),
                            )
                          }
                        >
                          {l}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <div className="repeat-grid month" role="group" aria-label="Days of the month">
                {Array.from({ length: 31 }, (_, i) => i + 1)
                  .concat(LAST_DAY)
                  .map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={d === LAST_DAY ? "wide" : undefined}
                      aria-pressed={monthDays.includes(d)}
                      onClick={() => setMonthDays(flip(monthDays, d, (a, b) => a === b))}
                    >
                      {d === LAST_DAY ? "Last day" : d}
                    </button>
                  ))}
              </div>
            )}
          </>
        ) : null}
        {unit === "year" ? <p className="muted">Each year on {shortDate(date).replace(/, \d{4}$/, "")}.</p> : null}
        {!bad && !empty ? (
          <p className="muted small">{repeatWords({ frequency: "custom", startDate: date, repeatRule: out })}</p>
        ) : null}
        {unit === "month" && !byWeekday && monthDays.some((d) => d > 28 && d < LAST_DAY) ? (
          <p className="muted small">Short months use their last day.</p>
        ) : null}
      </div>
    </Modal>
  );
}

export type FutureKey = keyof VisitFutureBody;
export const FUTURE_WORDS: [FutureKey, string][] = [
  ["time", "Time of day"],
  ["repeats", "Repeats"],
  ["crew", "Assigned team"],
  ["lines", "Line items"],
];

/** Apply visit settings to future visits: ticks for what every visit after this one takes from it. */
export function FutureSheet({
  date,
  ticked,
  saving,
  onClose,
  onSave,
}: {
  date: string;
  /** Ticked when it opens (what was just changed). */
  ticked: FutureKey[];
  saving: boolean;
  onClose: () => void;
  onSave: (b: VisitFutureBody) => void;
}) {
  const [on, setOn] = useState<Set<FutureKey>>(new Set(ticked));
  return (
    <Modal
      title="Apply visit settings to future visits"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            Just this visit
          </Button>
          <Button
            loading={saving}
            disabled={!on.size}
            onClick={() =>
              onSave({ time: on.has("time"), repeats: on.has("repeats"), crew: on.has("crew"), lines: on.has("lines") })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <p>
        Which settings from your visit on <b>{shortDate(date)}</b> should every visit after it take?
      </p>
      <div className="pick-list">
        {FUTURE_WORDS.map(([k, words]) => (
          <label key={k} className="pick">
            <input
              type="checkbox"
              checked={on.has(k)}
              onChange={() => {
                const n = new Set(on);
                if (n.has(k)) n.delete(k);
                else n.add(k);
                setOn(n);
              }}
            />
            <span>
              {words}
              {k === "repeats" ? <small className="muted"> (repeat on this visit's day from now on)</small> : null}
            </span>
          </label>
        ))}
      </div>
      <p className="muted small">Visits before it stay as they are. Done and billed visits never change.</p>
    </Modal>
  );
}
